import { FC, useState } from 'react';
import { logger } from '@belong/logging/logger';
import { Alert, ButtonWithRef, GenericFormLayout, GenericInput, Stack, useForm } from '@belong/ui-components';
import { ALERT_LEVELS, ALERT_POSITION } from '@belong/types';
import ChatAPI from '../../api';
import { LIVE_CHAT_TEST_IDS } from '../../lib/testIds';
import CONTENT from '../content.json';

type FeedbackCommentFormProps = {
  onSuccess: () => void;
  contactId?: string;
};

export const FeedbackCommentForm: FC<FeedbackCommentFormProps> = ({ contactId, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { FormRef, manageForm, errors } = useForm();

  const handleSubmit = () => {
    if (!contactId) {
      return;
    }

    setIsSubmitting(true);
    const { comment } = manageForm.getData<'comment'>();
    ChatAPI.sendFeedbackComment(contactId, comment)
      .then(response => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        } else {
          onSuccess();
        }
      })
      .catch(error => {
        logger.error(`Failed to submit chat feedback comment: ${error.message}`);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Stack space="medium">
      <Alert type={ALERT_LEVELS.SUCCESS} position={ALERT_POSITION.INLINE} hasFocus>
        {CONTENT.feedback.rating.success}
      </Alert>

      <GenericFormLayout handleSubmit={handleSubmit} validateForm={manageForm.validateForm} FormRef={FormRef}>
        <GenericInput
          name="comment"
          label={CONTENT.feedback.rating.question}
          errorValidation={[
            { errorKey: 'required', message: CONTENT.feedback.comment.required },
            { errorKey: 'maxLength', message: CONTENT.feedback.comment.exceedsLimit, max: 500 }
          ]}
          manageForm={manageForm}
          hasFormError={errors.hasFormError}
          data-testid={LIVE_CHAT_TEST_IDS.FEEDBACK_INPUT}
        />
        <ButtonWithRef
          type="submit"
          width="full"
          isLoading={isSubmitting}
          data-testid={LIVE_CHAT_TEST_IDS.FEEDBACK_SUBMIT}
        >
          {isSubmitting ? CONTENT.feedback.submitLabel.submitting : CONTENT.feedback.submitLabel.text}
        </ButtonWithRef>
      </GenericFormLayout>
    </Stack>
  );
};
